/**
 * `ConfirmDestructiveDialog` — reusable confirmation gate for
 * destructive/irreversible actions.
 *
 * Modeled on lexportal's `SoftDeleteOrgDialog` — same "type an exact
 * phrase to enable the primary" pattern the platform-admin surface has
 * used successfully. Ported here so the lawyer-facing SPA gates its own
 * destructive actions (channel disconnect, in the first pass; likely
 * more to follow — file delete, credential rotation, etc.).
 *
 * Contract:
 *   - Consumer supplies `open` / `onClose`, a title, a short
 *     description, a bullet list of concrete consequences, the exact
 *     `confirmPhrase` the user must type (localized by caller),
 *     the primary-button label, and an `onConfirm` callback that runs
 *     ONLY after the phrase matches (case-sensitive; `.trim()` on the
 *     user's input).
 *   - Optional `reason` prop = "audited"; when true, a required textarea
 *     is added and passed to `onConfirm(reason)`. Off by default
 *     (per-user destructive actions rarely need an audit reason; org-
 *     scale ones do — mirror the lexportal split).
 *   - `busy` locks the whole dialog during the async action.
 *   - `severity` swaps the color palette (red for `destructive`, amber
 *     for `warning` — the latter for reversible-but-visible actions).
 *   - State (typed phrase, reason) resets whenever `open` flips to
 *     false, so a re-open starts fresh.
 *
 * Non-goals:
 *   - No slug-keyed phrases yet (lexportal's `PermanentDeleteOrgDialog`
 *     shape). Add if any lawyer-facing action ever needs
 *     entity-specific gating.
 *   - No countdown timer / delayed-enable — the phrase gate is the
 *     entire friction.
 */

import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  dialogOverlayCls,
  dialogPanelCls,
  dialogTitleCls,
  dialogCloseBtnCls,
  dialogInputCls,
  dialogCancelBtnCls,
} from "@/components/context-pane/dialog-styles";

/** Pure helper — the confirm gate logic extracted so it can be tested
 *  without a DOM env. Returns the individual predicates + the composite
 *  `canConfirm`. Consumers use `canConfirm` for the primary-button
 *  disabled state; the individual predicates are exposed for surfaces
 *  that want to color-code the two gates separately. */
export interface ConfirmGateInput {
  typedConfirm: string;
  confirmPhrase: string;
  busy?: boolean;
  reason?: {
    text: string;
    minChars?: number;
    maxChars?: number;
    /** When true, reason is enabled + must pass length validation.
     *  When false, `reasonOk` is always true. */
    enabled: boolean;
  };
}
export interface ConfirmGate {
  phraseOk: boolean;
  reasonOk: boolean;
  canConfirm: boolean;
}
export function computeConfirmGate({ typedConfirm, confirmPhrase, busy, reason }: ConfirmGateInput): ConfirmGate {
  const phraseOk = typedConfirm.trim() === confirmPhrase;
  let reasonOk = true;
  if (reason?.enabled) {
    const trimmed = reason.text.trim();
    const min = reason.minChars ?? 3;
    const max = reason.maxChars ?? 500;
    reasonOk = trimmed.length >= min && trimmed.length <= max;
  }
  return { phraseOk, reasonOk, canConfirm: phraseOk && reasonOk && !busy };
}

export interface ConfirmDestructiveDialogProps {
  open: boolean;
  onClose: () => void;
  /** Title in the dialog header — e.g. "Desconectar Telegram". */
  title: string;
  /** Short one-sentence description under the title. */
  description?: string;
  /** Concrete consequences the user should read before typing the
   *  phrase. Rendered as a bulleted list inside the warning box. */
  bullets: readonly string[];
  /** The exact string the user must type. Case-sensitive after
   *  `.trim()`. Localized by the CALLER — e.g. `t("...confirmPhrase")`.
   *  pt-BR default: "EU ENTENDO"; en-US: "I UNDERSTAND"; es-ES:
   *  "COMPRENDO". */
  confirmPhrase: string;
  /** Label under the input field prompting the user to type. Should
   *  interpolate `{{phrase}}` — see i18n keys. */
  confirmPromptLabel: string;
  /** Primary button label — e.g. "Desconectar (irreversível)". */
  confirmButtonLabel: string;
  /** Secondary button label — usually "Cancelar". */
  cancelButtonLabel: string;
  /** Fires only when phrase matches + (if reason required) reason
   *  passes validation. `reason` is the trimmed textarea value when
   *  `reason` prop is enabled; `undefined` otherwise. */
  onConfirm: (reason?: string) => Promise<void> | void;
  /** Enable the audited-reason textarea. Off by default. */
  reason?: {
    label: string;
    placeholder?: string;
    /** Min chars after `.trim()`. Default 3. */
    minChars?: number;
    /** Max chars. Default 500. */
    maxChars?: number;
  };
  /** Disable all inputs + buttons during the async confirm. */
  busy?: boolean;
  /** Async error message from a prior confirm attempt. */
  error?: string | null;
  /** Color palette. `destructive` (default) = red; `warning` = amber. */
  severity?: "destructive" | "warning";
}

export function ConfirmDestructiveDialog({
  open,
  onClose,
  title,
  description,
  bullets,
  confirmPhrase,
  confirmPromptLabel,
  confirmButtonLabel,
  cancelButtonLabel,
  onConfirm,
  reason,
  busy = false,
  error = null,
  severity = "destructive",
}: ConfirmDestructiveDialogProps) {
  const [typedConfirm, setTypedConfirm] = useState("");
  const [reasonText, setReasonText] = useState("");
  const confirmInputRef = useRef<HTMLInputElement>(null);

  // Reset on close — a re-open starts fresh (matches lexportal's shape).
  useEffect(() => {
    if (!open) {
      setTypedConfirm("");
      setReasonText("");
    } else {
      // Auto-focus the phrase input on open so the friction is a keystroke
      // away — but not the primary action.
      window.setTimeout(() => confirmInputRef.current?.focus(), 60);
    }
  }, [open]);

  if (!open) return null;

  const reasonMin = reason?.minChars ?? 3;
  const reasonMax = reason?.maxChars ?? 500;
  const trimmedReason = reasonText.trim();
  const { canConfirm } = computeConfirmGate({
    typedConfirm,
    confirmPhrase,
    busy,
    reason: reason ? { text: reasonText, minChars: reasonMin, maxChars: reasonMax, enabled: true } : undefined,
  });

  // Palette by severity — red for destructive, amber for warning-only.
  const paletteBox =
    severity === "warning"
      ? "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
      : "border-red-300 bg-red-50 text-red-800 dark:border-red-700 dark:bg-red-950/40 dark:text-red-200";
  const paletteBtn =
    severity === "warning"
      ? "bg-amber-600 text-white hover:bg-amber-700 disabled:hover:bg-amber-600"
      : "bg-red-600 text-white hover:bg-red-700 disabled:hover:bg-red-600";
  const paletteIcon =
    severity === "warning"
      ? "text-amber-700 dark:text-amber-400"
      : "text-red-700 dark:text-red-400";

  return (
    <div className={cn(dialogOverlayCls, busy && "cursor-wait")}>
      <button
        type="button"
        aria-label={cancelButtonLabel}
        onClick={busy ? undefined : onClose}
        disabled={busy}
        className={cn("absolute inset-0 cursor-default", busy && "cursor-wait")}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-destructive-title"
        data-testid="confirm-destructive-dialog"
        data-severity={severity}
        className={cn(
          dialogPanelCls,
          "relative z-10 flex max-h-[90vh] w-full max-w-xl flex-col overflow-x-hidden overflow-y-hidden p-0",
        )}
      >
        <header className="flex shrink-0 items-center gap-2.5 border-b border-cream-200/70 px-4 py-3 dark:border-coffee-700/50">
          <AlertTriangle size={16} aria-hidden className={cn("shrink-0", paletteIcon)} />
          <div className="min-w-0 flex-1">
            <h2 id="confirm-destructive-title" className={cn(dialogTitleCls, "truncate")}>
              {title}
            </h2>
            {description && (
              <p className="truncate text-[11px] text-slate-500 dark:text-cream-400" title={description}>
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={busy ? undefined : onClose}
            disabled={busy}
            aria-label={cancelButtonLabel}
            className={cn(dialogCloseBtnCls, "disabled:cursor-wait disabled:opacity-50")}
          >
            <X size={14} />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 text-sm text-slate-700 dark:text-cream-200">
          {/* Consequences */}
          <div
            data-testid="confirm-destructive-bullets"
            className={cn("rounded-md border p-3 text-[12px]", paletteBox)}
          >
            <ul className="list-disc space-y-1 pl-4">
              {bullets.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          </div>

          {/* Phrase input */}
          <div className="mt-4 space-y-1">
            <label
              htmlFor="confirm-destructive-phrase-input"
              className="text-[12px] font-medium text-slate-600 dark:text-cream-300"
            >
              {confirmPromptLabel}{" "}
              <span
                data-testid="confirm-destructive-phrase-target"
                className="font-mono font-semibold text-coffee-800 dark:text-cream-100"
              >
                {confirmPhrase}
              </span>
            </label>
            <input
              id="confirm-destructive-phrase-input"
              ref={confirmInputRef}
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={typedConfirm}
              onChange={(e) => setTypedConfirm(e.target.value)}
              placeholder={confirmPhrase}
              disabled={busy}
              data-testid="confirm-destructive-phrase-input"
              className={cn(dialogInputCls, "font-mono disabled:opacity-60")}
            />
          </div>

          {/* Optional audited reason */}
          {reason && (
            <div className="mt-4 space-y-1">
              <label
                htmlFor="confirm-destructive-reason-input"
                className="text-[12px] font-medium text-slate-600 dark:text-cream-300"
              >
                {reason.label}
                <span className="ml-1 text-slate-400 dark:text-slate-500">
                  ({reasonMin}-{reasonMax})
                </span>
              </label>
              <textarea
                id="confirm-destructive-reason-input"
                data-testid="confirm-destructive-reason-input"
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                rows={3}
                placeholder={reason.placeholder ?? ""}
                disabled={busy}
                className={cn(dialogInputCls, "disabled:opacity-60")}
              />
            </div>
          )}

          {error && (
            <div
              data-testid="confirm-destructive-error"
              className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-[12px] text-red-800 dark:border-red-700 dark:bg-red-950/40 dark:text-red-200"
            >
              {error}
            </div>
          )}
        </div>

        <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-cream-200/70 bg-cream-50/70 px-4 py-3 dark:border-coffee-700/50 dark:bg-coffee-950/40">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="confirm-destructive-cancel"
            className={dialogCancelBtnCls}
          >
            {cancelButtonLabel}
          </button>
          <button
            type="button"
            onClick={() => void onConfirm(reason ? trimmedReason : undefined)}
            disabled={!canConfirm}
            data-testid="confirm-destructive-confirm"
            className={cn(
              "inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-body-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40",
              paletteBtn,
            )}
          >
            {busy && <Loader2 size={12} aria-hidden className="animate-spin" />}
            {confirmButtonLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}
